import { NgModule } from '@angular/core';
import { RouterModule, Routes, TitleStrategy } from '@angular/router';

import { AuthGuard } from '@bn/auth/guards';
import { UserStateResolver } from '@bn/auth/resolvers';
import { AccountSuspendedGuard, EmailNotVerifiedGuard, FeatureFlagGuard, liveStreamEnabledGuard, UserPermissionsGuard } from '@bn/guards';
import { AIGatewayRoutes, AppRoutes, FeatureFlags, ORIGIN_PATHS, StreamAppRoutes } from '@bn/primitives';
import { BnTemplatePageTitleStrategy } from '@bn/shared/core/services';

import { DashboardLayoutComponent } from './layout/components';
import { DemoComponentsPage } from './pages/components/demo-components.page';
import { LogoutPage } from './pages/logout/logout.page';

export const appRoutes: Routes = [
  {
    path: AppRoutes.Auth,
    loadChildren: () => import('@bn/auth-pages').then((module) => module.AuthModule),
    title: 'bunny.net - The Content Delivery platform that truly Hops!',
  },
  {
    path: `${StreamAppRoutes.livestreamPreview}/:videoLibraryId/:livestreamId`,
    loadComponent: () =>
      import('@bn/products/stream/pages/edit-video-library/livestream-preview/edit-video-library-livestream-preview.page').then(
        (m) => m.EditVideoLibraryLivestreamPreviewPage,
      ),
    canActivate: [AuthGuard, AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard, FeatureFlagGuard, liveStreamEnabledGuard],
    data: {
      featureFlag: FeatureFlags.livestream,
      roleIds: ['SubuserManage'],
    },
    title: 'Livestream Preview',
  },
  {
    path: 'deploy',
    loadComponent: () => import('@bn/products/mc/pages/deploy').then((m) => m.MCDeployPage),
    title: 'Deploy',
  },
  {
    path: `${AppRoutes.Partner}/accept-invite`,
    loadComponent: () => import('./pages/partner/pages/accept-invite/accept-invite.page').then((m) => m.AcceptInvitePage),
    canActivate: [AuthGuard],
    title: 'Accept Partner Invitation',
  },
  {
    path: `${AppRoutes.Zendesk}/login`,
    loadComponent: () => import('./pages/zendesk/pages/login/zendesk-login-page/zendesk-login.page').then((m) => m.ZendeskLoginPage),
    canActivate: [AuthGuard],
    title: 'Zendesk - Login',
  },
  {
    path: '',
    canActivate: [AuthGuard],
    component: DashboardLayoutComponent,
    resolve: {
      userDetails: UserStateResolver,
    },
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/overview/overview.module').then((module) => module.OverviewModule),
        canActivate: [AccountSuspendedGuard],
        // pathMatch: 'full',
        title: 'Account Overview',
      },
      {
        path: AppRoutes.Welcome,
        loadChildren: () => import('./pages/overview/overview.module').then((module) => module.OverviewModule),
        canActivate: [AccountSuspendedGuard],
        pathMatch: 'full',
        title: 'Welcome',
      },
      {
        path: AppRoutes.CDN,
        loadChildren: () => import('@bn/products/cdn').then((module) => module.CdnModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Pull Zones',
      },
      {
        path: AppRoutes.Storage,
        loadChildren: () => import('@bn/products/storage').then((module) => module.StorageModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Storage Zones',
      },
      {
        path: AppRoutes.DNS,
        loadChildren: () => import('@bn/products/dns').then((module) => module.DnsModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'DNS Zones',
      },
      {
        path: AppRoutes.Database,
        loadChildren: () => import('@bn/products/database').then((module) => module.DatabaseModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Database',
      },
      {
        path: AppRoutes.Script,
        loadChildren: () => import('@bn/products/scripts').then((module) => module.ScriptModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Scripts',
      },
      {
        path: AppRoutes.MagicContainers,
        loadChildren: () => import('@bn/products/mc').then((module) => module.MagicContainersModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Magic Containers',
      },
      {
        path: AppRoutes.AIGateway,
        redirectTo: `${AppRoutes.AIGateway}/${AIGatewayRoutes.Overview}`,
        pathMatch: 'full',
      },
      {
        path: AppRoutes.AIGateway,
        loadChildren: () => import('@bn/products/ai-gateway').then((module) => module.AIGatewayModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard, FeatureFlagGuard],
        data: {
          roleIds: ['SubuserManage'],
          featureFlag: FeatureFlags.aiGateway,
        },
        title: 'AI Gateway',
      },
      {
        path: 'deploy',
        loadComponent: () => import('@bn/products/mc/pages/deploy').then((m) => m.MCDeployPage),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Deploy',
      },
      /*      {
        path: `${AppRoutes.MagicContainers}/waitlist`,
        component: MagicContainersPublicBetaPage,
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard, NegatedFeatureFlagGuard],
        data: {
          roleIds: ['SubuserManage'],
          featureFlag: FeatureFlags.mc,
        },
        title: 'Magic containers',
      },*/
      {
        path: AppRoutes.Stream,
        loadChildren: () => import('@bn/products/stream').then((module) => module.streamRoutes),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Stream',
      },
      {
        path: AppRoutes.Statistics,
        loadChildren: () => import('@bn/products/statistics').then((module) => module.StatisticsModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Statistics',
      },
      {
        path: ORIGIN_PATHS.base,
        loadChildren: () => import('@bn/products/origin-error').then((module) => module.EXCHANGE_ROUTES),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: { roleIds: ['SubuserManage'] },
        title: BnTemplatePageTitleStrategy.TITLE_IGNORE,
      },
      {
        path: AppRoutes.Purge,
        loadChildren: () => import('@bn/products/purge').then((module) => module.PurgeModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Purge',
      },
      {
        path: AppRoutes.Logs,
        loadChildren: () => import('@bn/products/logs').then((module) => module.routes),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserManage'],
        },
        title: 'Log explorer',
      },
      {
        path: AppRoutes.Surveys,
        loadChildren: () => import('@bn/products/surveys').then((module) => module.SurveysModule),
        canActivate: [AccountSuspendedGuard, UserPermissionsGuard, EmailNotVerifiedGuard, FeatureFlagGuard],
        data: {
          roleIds: ['Agent'],
          featureFlag: FeatureFlags.surveys,
        },
        title: 'Surveys',
      },
      {
        path: AppRoutes.Notifications,
        loadChildren: () => import('./pages/notifications/notifications.module').then((module) => module.NotificationsModule),
        canActivate: [AccountSuspendedGuard],
        title: 'Changelog & Notifications',
      },
      {
        path: AppRoutes.Account,
        loadChildren: () => import('./pages/account/account.module').then((module) => module.AccountModule),
        title: 'Account settings',
      },
      {
        path: AppRoutes.Support,
        loadChildren: () => import('./pages/support/support.module').then((module) => module.SupportModule),
        canActivate: [UserPermissionsGuard, EmailNotVerifiedGuard],
        data: {
          roleIds: ['SubuserSupport'],
        },
        title: 'Support',
      },
      {
        path: AppRoutes.Errors,
        loadChildren: () => import('@bn/products/errors').then((module) => module.ErrorsModule),
        title: 'Errors',
      },
      {
        path: 'logout',
        component: LogoutPage,
        title: 'Logout',
      },
      {
        path: 'components',
        component: DemoComponentsPage,
        title: 'Demo Components',
      },
      {
        path: AppRoutes.NotFound,
        loadComponent: () => import('@bn/products/errors/pages/not-found/not-found.page').then((module) => module.NotFoundPage),
        title: 'Page Not Found',
      },
    ],
  },
  {
    path: '404',
    loadComponent: () => import('@bn/products/errors/pages/not-found/not-found.page').then((module) => module.NotFoundPage),
    title: 'Page Not Found',
  },
  { path: AppRoutes.NotFound, redirectTo: '' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      paramsInheritanceStrategy: 'always',
      anchorScrolling: 'enabled',
      scrollOffset: [0, 80],
      // enableTracing: true, // <-- debugging purposes only
      bindToComponentInputs: true,
    }),
  ],
  exports: [RouterModule],
  providers: [{ provide: TitleStrategy, useClass: BnTemplatePageTitleStrategy }],
})
export class AppRoutingModule {}
